import 'twin.macro';
import React from 'react';
import { EleveSearchResult } from '@teetsh/app/src/containers/Search/types';
import { Avatar } from '@teetsh/app/src/components/AvatarCard/Avatar';
import { getGenderLabelKey } from '@teetsh/app/src/queries/eleves/serializer';
import { Gender } from '@teetsh/app/src/types/gender';
import { format } from '@teetsh/app/src/utils/time/format';
import { CustomFieldDisplayer } from '../components/CustomFieldDisplayer';
import { frenchDatePatternWithSlashes } from 'utils/time/datePatterns';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

interface Props {
  eleve: EleveSearchResult;
}

type FieldType = 'birthday' | 'gender' | 'classroomName' | 'groupName' | 'ine';
interface Field {
  key: FieldType;
  applyFunction?: (
    v: any,
    t?: TFunction<['common', 'eleve'], undefined>,
  ) => string;
}

const fields: Field[] = [
  {
    key: 'birthday',
    applyFunction: (d: Date) =>
      format(d, 'fr-FR', frenchDatePatternWithSlashes),
  },
  {
    key: 'gender',
    applyFunction: (v: Gender, t) => {
      const labelKey = getGenderLabelKey(v);
      if (!t || labelKey === null) return labelKey || '';
      return t(`eleve:fields.${labelKey}`);
    },
  },
  { key: 'classroomName' },
  { key: 'groupName' },
  { key: 'ine' },
];

export function ElevePreview(props: Props) {
  const { eleve } = props;
  const { t } = useTranslation(['common', 'eleve']);
  return (
    <div tw="p-6">
      <div>
        <div tw="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div tw="sm:flex sm:items-end sm:space-x-5">
            <div tw="flex">
              <Avatar
                picture={eleve.picture}
                dimensions={{ width: 96, height: 96 }}
                tw="ring-4 ring-white rounded-full"
              />
            </div>
            <div tw="mt-6 sm:flex sm:min-w-0 sm:flex-1 sm:items-center sm:justify-end sm:space-x-6 sm:pb-1">
              <div tw="mt-6 min-w-0 flex-1 sm:hidden 2xl:block">
                <h1 tw="truncate text-2xl font-bold text-neutral-intense">
                  {eleve.familyName} {eleve.givenName}
                </h1>
              </div>
              <div tw="justify-self-stretch mt-6 flex flex-col space-y-3 sm:flex-row sm:space-y-0 sm:space-x-4"></div>
            </div>
          </div>
          <div tw="mt-6 hidden min-w-0 flex-1 sm:block 2xl:hidden">
            <h1 tw="truncate text-2xl font-bold text-neutral-intense">
              {eleve.familyName} {eleve.givenName}
            </h1>
          </div>
        </div>
      </div>
      <div tw="mx-auto mt-6 max-w-5xl px-4 sm:px-6 lg:px-8">
        <dl tw="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
          {fields.map(({ key, applyFunction }) => {
            const value = eleve[key];
            if (!value) return null;
            return (
              <div key={key} tw="sm:col-span-1">
                <dt tw="text-sm font-medium text-neutral-quiet">
                  {t(`common:search.results.eleve.${key}`)}
                </dt>
                <dd tw="mt-1 text-sm text-neutral-intense">
                  {applyFunction ? applyFunction(value, t) : value}
                </dd>
              </div>
            );
          })}
          {(eleve?.customFields || []).map(c => (
            <div key={c.id} tw="sm:col-span-1">
              <dt tw="text-sm font-medium text-neutral-quiet">{c.name}</dt>
              <dd tw="mt-1 text-sm text-neutral-intense">
                <CustomFieldDisplayer
                  fieldType={c.type}
                  value={c.value}
                  options={(c as any).options}
                />
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}
